package br.com.texeratx.boardgametimeapp.ui.screens.games.detail

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.EmojiEvents
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.ExpandLess
import androidx.compose.material.icons.filled.ExpandMore
import androidx.compose.material.icons.filled.Groups
import androidx.compose.material.icons.filled.Insights
import androidx.compose.material.icons.filled.MusicNote
import androidx.compose.material.icons.filled.PictureAsPdf
import androidx.compose.material.icons.filled.Public
import androidx.compose.material.icons.filled.Schedule
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.material3.TopAppBarScrollBehavior
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.input.nestedscroll.nestedScroll
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.texeratx.boardgametimeapp.ui.components.GameThumb
import br.com.texeratx.boardgametimeapp.ui.components.PlayerAvatar
import br.com.texeratx.boardgametimeapp.ui.screens.games.GameCover
import br.com.texeratx.boardgametimeapp.ui.screens.matches.model.MatchListItemUI
import br.com.texeratx.boardgametimeapp.ui.utils.apiToUiDate
import br.com.texeratx.boardgametimeapp.ui.utils.apiToUiDateTime
import br.com.texeratx.boardgametimeapp.ui.utils.minutesToHourMin
import br.com.texeratx.boardgametimeapp.core.i18n.LocalStrings
import cafe.adriel.voyager.navigator.LocalNavigator
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppTopBarColors
import kotlin.math.abs
import kotlin.math.max
import kotlin.math.roundToInt

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun GameDetailUI(
    game: GameDetailUIModel,
    modifier: Modifier = Modifier,
    contentPadding: PaddingValues = PaddingValues(0.dp),
    onEditClick: () -> Unit,
    onDeleteClick: () -> Unit,
    showDeleteConfirmation: Boolean,
    isDeleting: Boolean,
    onDismissDeleteConfirmation: () -> Unit,
    onConfirmDelete: () -> Unit,
    onCreateExpansionClick: () -> Unit,
    onMatchClick: (MatchListItemUI) -> Unit
) {
    val navigator = LocalNavigator.current
    val uriHandler = LocalUriHandler.current
    val scrollBehavior = TopAppBarDefaults.enterAlwaysScrollBehavior()
    val S = LocalStrings.current
    var statsExpanded by remember { mutableStateOf(true) }
    var scoreStatsExpanded by remember { mutableStateOf(true) }
    var winnersExpanded by remember { mutableStateOf(true) }
    var ownersExpanded by remember { mutableStateOf(true) }
    var matchHistoryExpanded by remember { mutableStateOf(true) }

    if (showDeleteConfirmation) {
        AlertDialog(
            onDismissRequest = onDismissDeleteConfirmation,
            title = { Text(S.deleteGame) },
            text = { Text(S.deleteGameConfirm) },
            confirmButton = {
                TextButton(
                    onClick = onConfirmDelete,
                    enabled = !isDeleting
                ) {
                    Text(S.delete)
                }
            },
            dismissButton = {
                TextButton(
                    onClick = onDismissDeleteConfirmation,
                    enabled = !isDeleting
                ) {
                    Text(S.cancel)
                }
            }
        )
    }

    Column(
        modifier = modifier
            .fillMaxSize()
            .nestedScroll(scrollBehavior.nestedScrollConnection)
    ) {
        GameDetailTopBar(
            title = S.gameDetailTitle,
            onBack = { navigator?.pop() },
            onEdit = onEditClick,
            onDelete = onDeleteClick,
            scrollBehavior = scrollBehavior
        )

        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .padding(contentPadding)
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            Row(verticalAlignment = Alignment.Top) {
                if (game.imageUrl.isNullOrBlank()) {
                    GameCover(
                        imageUrl = game.imageUrl,
                        title = game.name,
                        modifier = Modifier
                            .size(220.dp)
                    )
                } else {
                    GameThumb(
                        imageUrl = game.imageUrl,
                        contentDescription = game.name,
                        size = 220.dp,
                        contentScale = ContentScale.Fit,
                        modifier = Modifier
                            .size(220.dp)
                            .clip(RoundedCornerShape(16.dp))
                    )
                }

                Spacer(Modifier.width(12.dp))

                Column(
                    verticalArrangement = Arrangement.spacedBy(12.dp),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (!game.bggUrl.isNullOrBlank()) {
                        LinkIcon(
                            icon = Icons.Default.Public,
                            description = "BGG",
                            onClick = { uriHandler.openUri(game.bggUrl) }
                        )
                    }

                    if (!game.manualUrl.isNullOrBlank()) {
                        LinkIcon(
                            icon = Icons.Default.PictureAsPdf,
                            description = "Manual",
                            onClick = { uriHandler.openUri(game.manualUrl) }
                        )
                    }

                    if (!game.spotifyUrl.isNullOrBlank()) {
                        LinkIcon(
                            icon = Icons.Default.MusicNote,
                            description = "Spotify",
                            onClick = { uriHandler.openUri(game.spotifyUrl) }
                        )
                    }
                }
            }

            Text(
                text = game.name,
                style = MaterialTheme.typography.headlineMedium,
                fontWeight = FontWeight.Bold
            )

            InfoCard {
                InfoRow(S.labelCategory, game.category)
                InfoRow(S.labelMode, game.mode)
                InfoRow(S.labelPlayers, game.playerRangeLabel())
                InfoRow(S.labelAvgDuration, game.duration?.let(::minutesToHourMin) ?: "-")
            }

            Button(
                onClick = onCreateExpansionClick,
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(16.dp),
                colors = ButtonDefaults.buttonColors(
                    containerColor = MaterialTheme.colorScheme.primary
                )
            ) {
                Icon(
                    imageVector = Icons.Default.Add,
                    contentDescription = S.createExpansionBtn
                )
                Spacer(Modifier.width(10.dp))
                Text(S.createExpansionBtn)
            }

            InfoCard {
                ExpandableSectionHeader(
                    title = S.sectionStatistics,
                    icon = Icons.Default.Insights,
                    expanded = statsExpanded,
                    onToggle = { statsExpanded = !statsExpanded }
                )

                if (statsExpanded) {
                    Spacer(Modifier.height(12.dp))

                    CompactStatPairRow(
                        leftTitle = S.statMatches,
                        leftValue = game.stats.matchesPlayed.toString(),
                        rightTitle = S.statThisYear,
                        rightValue = game.stats.matchesPlayedCurrentYear.toString()
                    )

                    Spacer(Modifier.height(12.dp))

                    CompactStatPairRow(
                        leftTitle = S.statLastMatch,
                        leftValue = game.stats.lastPlayedDate ?: "-",
                        rightTitle = S.statFirstMatch,
                        rightValue = game.stats.firstPlayedDate ?: "-",
                        compactValue = true
                    )

                    Spacer(Modifier.height(12.dp))

                    DurationStatsCard(
                        averageDuration = game.stats.averageDurationInMinutes?.let(::minutesToHourMin) ?: "-",
                        longestDuration = game.stats.longestDurationInMinutes?.let(::minutesToHourMin) ?: "-",
                        shortestDuration = game.stats.shortestDurationInMinutes?.let(::minutesToHourMin) ?: "-"
                    )
                }
            }

            InfoCard {
                ExpandableSectionHeader(
                    title = S.sectionScoreStats,
                    icon = Icons.Default.EmojiEvents,
                    expanded = scoreStatsExpanded,
                    onToggle = { scoreStatsExpanded = !scoreStatsExpanded }
                )

                if (scoreStatsExpanded) {
                    Spacer(Modifier.height(12.dp))

                    ScoreStatsBars(game = game)
                }
            }

            InfoCard {
                ExpandableSectionHeader(
                    title = S.sectionGameWinners,
                    icon = Icons.Default.EmojiEvents,
                    expanded = winnersExpanded,
                    onToggle = { winnersExpanded = !winnersExpanded }
                )

                if (winnersExpanded) {
                    Spacer(Modifier.height(12.dp))

                    if (game.winners.isEmpty()) {
                        EmptySectionText(S.emptyGameWinners)
                    } else {
                        game.winners.forEachIndexed { index, winner ->
                            WinnerRow(
                                winner = winner,
                                winsLabel = S.gameWinnerWinsFormat(winner.wins)
                            )
                            if (index != game.winners.lastIndex) {
                                Spacer(Modifier.height(8.dp))
                            }
                        }
                    }
                }
            }

            InfoCard {
                ExpandableSectionHeader(
                    title = S.sectionOwners,
                    icon = Icons.Default.Groups,
                    expanded = ownersExpanded,
                    onToggle = { ownersExpanded = !ownersExpanded }
                )

                if (ownersExpanded) {
                    Spacer(Modifier.height(12.dp))

                    if (game.owners.isEmpty()) {
                        EmptySectionText(S.emptyOwners)
                    } else {
                        game.owners.forEach { owner ->
                            OwnerRow(owner = owner)
                        }
                    }
                }
            }

            InfoCard {
                ExpandableSectionHeader(
                    title = S.sectionMatchHistory,
                    icon = Icons.Default.Schedule,
                    expanded = matchHistoryExpanded,
                    onToggle = { matchHistoryExpanded = !matchHistoryExpanded }
                )

                if (matchHistoryExpanded) {
                    Spacer(Modifier.height(12.dp))

                    if (game.matches.isEmpty()) {
                        EmptySectionText(S.emptyMatchHistory)
                    } else {
                        game.matches.forEachIndexed { index, match ->
                            GameMatchCard(
                                match = match,
                                onClick = { onMatchClick(match) }
                            )
                            if (index != game.matches.lastIndex) {
                                Spacer(Modifier.height(12.dp))
                            }
                        }
                    }
                }
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun GameDetailTopBar(
    title: String,
    onBack: () -> Unit,
    onEdit: () -> Unit,
    onDelete: () -> Unit,
    scrollBehavior: TopAppBarScrollBehavior
) {
    val S = LocalStrings.current
    TopAppBar(
        title = {
            Text(
                text = title,
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.Bold
            )
        },
        navigationIcon = {
            Icon(
                imageVector = Icons.Default.ArrowBack,
                contentDescription = S.back,
                modifier = Modifier
                    .padding(start = 12.dp)
                    .clickable { onBack() }
            )
        },
        actions = {
            IconButton(onClick = onEdit) {
                Icon(
                    imageVector = Icons.Default.Edit,
                    contentDescription = S.editGame
                )
            }
            IconButton(
                onClick = onDelete,
                modifier = Modifier.padding(end = 4.dp)
            ) {
                Icon(
                    imageVector = Icons.Default.Delete,
                    contentDescription = S.deleteGame
                )
            }
        },
        colors = AppTopBarColors(),
        scrollBehavior = scrollBehavior
    )
}

@Composable
fun InfoCard(content: @Composable ColumnScope.() -> Unit) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(MaterialTheme.colorScheme.surfaceVariant)
            .padding(16.dp)
    ) {
        content()
    }
}

@Composable
fun InfoRow(
    label: String,
    value: String?
) {
    if (value.isNullOrBlank()) return

    Row(
        modifier = Modifier.padding(vertical = 4.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(
            text = "$label:",
            style = MaterialTheme.typography.bodyMedium,
            fontWeight = FontWeight.SemiBold,
            modifier = Modifier.width(120.dp)
        )

        Text(
            text = value,
            style = MaterialTheme.typography.bodyMedium
        )
    }
}

@Composable
fun LinkIcon(
    icon: ImageVector,
    description: String,
    onClick: () -> Unit
) {
    Box(
        modifier = Modifier
            .clip(RoundedCornerShape(40))
            .background(MaterialTheme.colorScheme.primary.copy(alpha = 0.15f))
            .clickable { onClick() }
            .padding(12.dp)
    ) {
        Icon(
            imageVector = icon,
            contentDescription = description,
            tint = MaterialTheme.colorScheme.primary
        )
    }
}

@Composable
private fun ExpandableSectionHeader(
    title: String,
    icon: ImageVector,
    expanded: Boolean,
    onToggle: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable { onToggle() },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Row(
            modifier = Modifier.weight(1f),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Icon(
                imageVector = icon,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.primary
            )
            Text(
                text = title,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold
            )
        }

        Icon(
            imageVector = if (expanded) Icons.Default.ExpandLess else Icons.Default.ExpandMore,
            contentDescription = null,
            tint = MaterialTheme.colorScheme.primary
        )
    }
}

@Composable
private fun DetailChip(text: String) {
    Surface(
        shape = RoundedCornerShape(999.dp),
        color = MaterialTheme.colorScheme.secondaryContainer
    ) {
        Text(
            text = text,
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onSecondaryContainer,
            fontWeight = FontWeight.SemiBold
        )
    }
}

@Composable
private fun CompactStatPairRow(
    leftTitle: String,
    leftValue: String,
    rightTitle: String,
    rightValue: String,
    compactValue: Boolean = false
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        CompactStatCard(
            title = leftTitle,
            value = leftValue,
            compactValue = compactValue,
            modifier = Modifier.weight(1f)
        )
        CompactStatCard(
            title = rightTitle,
            value = rightValue,
            compactValue = compactValue,
            modifier = Modifier.weight(1f)
        )
    }
}

@Composable
private fun CompactStatCard(
    title: String,
    value: String,
    compactValue: Boolean = false,
    modifier: Modifier = Modifier
) {
    Card(modifier = modifier) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 12.dp, vertical = 10.dp),
            verticalArrangement = Arrangement.spacedBy(4.dp)
        ) {
            Text(
                text = title,
                style = MaterialTheme.typography.labelLarge
            )
            Text(
                text = value,
                style = if (compactValue) {
                    MaterialTheme.typography.bodySmall
                } else {
                    MaterialTheme.typography.titleLarge
                },
                color = MaterialTheme.colorScheme.primary,
                fontWeight = FontWeight.Bold
            )
        }
    }
}

@Composable
private fun DurationStatsCard(
    averageDuration: String,
    longestDuration: String,
    shortestDuration: String
) {
    Card {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(12.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                Icon(
                    imageVector = Icons.Default.Schedule,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.primary
                )
                Text(
                    text = LocalStrings.current.durationLabel,
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.Bold
                )
            }

            InfoRow(LocalStrings.current.statAverageMatchDuration, averageDuration)
            InfoRow(LocalStrings.current.statLongestMatch, longestDuration)
            InfoRow(LocalStrings.current.statShortestMatch, shortestDuration)
        }
    }
}

@Composable
private fun ScoreStatsBars(game: GameDetailUIModel) {
    val S = LocalStrings.current
    val highest = game.scoreStats.highestScore
    val winnerAvg = game.scoreStats.winnerAverageScore
    val lowest = game.scoreStats.lowestScore
    val maxReference = max(
        abs(highest ?: 0.0),
        max(abs(winnerAvg ?: 0.0), abs(lowest ?: 0.0))
    ).takeIf { it > 0.0 }

    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
        ScoreBarMetricRow(
            title = S.statHighestScore,
            value = highest,
            supporting = formatScorePlayers(game.scoreStats.highestScorePlayers),
            maxReference = maxReference,
            color = MaterialTheme.colorScheme.primary
        )
        ScoreBarMetricRow(
            title = S.statWinnerAverageScore,
            value = winnerAvg,
            supporting = null,
            maxReference = maxReference,
            color = MaterialTheme.colorScheme.tertiary
        )
        ScoreBarMetricRow(
            title = S.statLowestScore,
            value = lowest,
            supporting = formatScorePlayers(game.scoreStats.lowestScorePlayers),
            maxReference = maxReference,
            color = MaterialTheme.colorScheme.secondary
        )
    }
}

@Composable
private fun ScoreBarMetricRow(
    title: String,
    value: Double?,
    supporting: String?,
    maxReference: Double?,
    color: androidx.compose.ui.graphics.Color
) {
    val widthFraction = if (value == null || maxReference == null) {
        0f
    } else {
        ((abs(value) / maxReference).toFloat()).coerceIn(0.18f, 1f)
    }

    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        Text(
            text = title,
            style = MaterialTheme.typography.labelLarge,
            fontWeight = FontWeight.SemiBold
        )

        if (value == null) {
            Text(
                text = "-",
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        } else {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(10.dp))
                    .background(MaterialTheme.colorScheme.surface.copy(alpha = 0.55f))
            ) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth(widthFraction)
                        .clip(RoundedCornerShape(10.dp))
                        .background(color.copy(alpha = 0.90f))
                        .padding(horizontal = 12.dp, vertical = 8.dp),
                    contentAlignment = Alignment.CenterEnd
                ) {
                    Text(
                        text = formatScoreOneDecimal(value),
                        style = MaterialTheme.typography.titleSmall,
                        color = MaterialTheme.colorScheme.onPrimary,
                        fontWeight = FontWeight.Bold
                    )
                }
            }
        }

        if (!supporting.isNullOrBlank() && supporting != "-") {
            Text(
                text = supporting,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}

@Composable
private fun WinnerRow(
    winner: GameDetailWinnerUI,
    winsLabel: String
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 6.dp),
        horizontalArrangement = Arrangement.spacedBy(12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        PlayerAvatar(
            imageUrl = winner.imageUrl,
            contentDescription = winner.name,
            size = 40.dp,
            modifier = Modifier.size(40.dp)
        )

        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = winner.nickname?.takeIf { it.isNotBlank() } ?: winner.name,
                style = MaterialTheme.typography.titleSmall,
                fontWeight = FontWeight.SemiBold
            )

            if (!winner.nickname.isNullOrBlank() && winner.nickname != winner.name) {
                Text(
                    text = winner.name,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }

        DetailChip(winsLabel)
    }
}

@Composable
private fun OwnerRow(owner: GameDetailOwnerUI) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 6.dp),
        horizontalArrangement = Arrangement.spacedBy(12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        PlayerAvatar(
            imageUrl = owner.imageUrl,
            contentDescription = owner.name,
            size = 52.dp,
            modifier = Modifier.size(52.dp)
        )

        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = owner.name,
                style = MaterialTheme.typography.titleSmall,
                fontWeight = FontWeight.SemiBold
            )

            val secondary = listOfNotNull(
                owner.nickname?.takeIf { it.isNotBlank() }?.let { "@$it" },
                owner.birthday?.let { apiToUiDate(it) }.takeIf { it != "-" },
                owner.phone?.takeIf { it.isNotBlank() }
            ).joinToString(" • ")

            if (secondary.isNotBlank()) {
                Text(
                    text = secondary,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
    }
}

@Composable
private fun GameMatchCard(
    match: MatchListItemUI,
    onClick: () -> Unit
) {
    val S = LocalStrings.current
    val orderedPlayers = match.players.sortedByDescending { it.points }

    Card(
        modifier = Modifier
            .fillMaxWidth()
            .border(
                width = 1.dp,
                color = MaterialTheme.colorScheme.outlineVariant,
                shape = RoundedCornerShape(16.dp)
            )
            .clickable { onClick() }
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(14.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text = apiToUiDateTime(match.date),
                        style = MaterialTheme.typography.titleSmall,
                        fontWeight = FontWeight.Bold
                    )
                    Text(
                        text = S.matchDurationDisplay(minutesToHourMin(match.duration)),
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }

                if (match.expansionIds.isNotEmpty()) {
                    DetailChip(text = S.expansionCountFormat(match.expansionIds.size))
                }
            }

            orderedPlayers.forEachIndexed { index, player ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(10.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    PlayerAvatar(
                        imageUrl = player.imageUrl,
                        contentDescription = player.name,
                        size = 34.dp,
                        modifier = Modifier.size(34.dp)
                    )

                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            text = player.nickname ?: player.name,
                            style = MaterialTheme.typography.bodyMedium,
                            fontWeight = if (index == 0) FontWeight.Bold else FontWeight.Medium,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                        Text(
                            text = player.name,
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }

                    Text(
                        text = "${player.points} pts",
                        style = MaterialTheme.typography.bodyMedium,
                        fontWeight = if (index == 0) FontWeight.Bold else FontWeight.Normal,
                        color = if (index == 0) {
                            MaterialTheme.colorScheme.primary
                        } else {
                            MaterialTheme.colorScheme.onSurface
                        }
                    )
                }
            }
        }
    }
}

@Composable
private fun EmptySectionText(text: String) {
    Text(
        text = text,
        style = MaterialTheme.typography.bodyMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant
    )
}

private fun GameDetailUIModel.playerRangeLabel(): String {
    return when {
        minPlayers == null && maxPlayers == null -> "-"
        minPlayers != null && maxPlayers != null -> "$minPlayers - $maxPlayers"
        minPlayers != null -> "$minPlayers+"
        else -> maxPlayers.toString()
    }
}

private fun formatScoreOneDecimal(value: Double): String {
    val normalized = (value * 10.0).roundToInt() / 10.0
    return "${normalized.toString().replace(',', '.')}"
}

private fun formatScorePlayers(players: List<GameDetailScorePlayerUI>): String {
    if (players.isEmpty()) return "-"
    return players.joinToString(", ") { it.name }
}
